import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { CalendarDays, Check, ChevronDown, ChevronUp, CircleDot, Dumbbell, Timer } from "lucide-react"
import { DateTime } from "luxon"
import type React from "react"
import { useState } from "react"
import { Badge } from "../components/ui/badge"
import { Button } from "../components/ui/button"
import { Calendar } from "../components/ui/calendar"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog"
import { Skeleton } from "../components/ui/skeleton"
import { fetchWorkoutsQueryOptions, useWorkouts } from "../hooks/useWorkouts"
import { apiFetch } from "../utils/apiFetch"
import {
  calculateExerciseWeight,
  calculateTotalWeight,
  displayDuration,
  type TExercise,
  type TExerciseSet,
  type TWorkout,
} from "../utils/workoutUtils"

export const Route = createFileRoute("/_app/calendar")({
  component: WorkoutCalendar,
})

const templateColors: Record<string, string> = {
  default: "bg-gray-300",
  green: "bg-green-300",
  blue: "bg-blue-300",
  purple: "bg-purple-300",
  pink: "bg-pink-300",
  yellow: "bg-yellow-300",
  orange: "bg-orange-300",
  red: "bg-red-300",
  cyan: "bg-cyan-300",
}

interface DayWithWorkoutProps {
  day: DateTime
  workouts: Array<TWorkout>
}

/**
 * Renders a dot for each workout scheduled on the given day.
 */
const DayWithWorkout = ({ day, workouts }: DayWithWorkoutProps) => {
  // Find workouts for this day
  const workoutsOnDay = workouts.filter((workout) => {
    const workoutDate = DateTime.fromISO(workout.dateScheduled)
    // compare only year/month/day against selectedDate
    return workoutDate.hasSame(day, "day", { useLocaleWeeks: false })
  })

  if (workoutsOnDay.length === 0) {
    return null
  }

  return (
    <div className="absolute right-0 bottom-0 left-0 flex justify-center gap-0.5 pb-1">
      {workoutsOnDay.map((workout) => {
        return (
          <div
            key={workout.workoutId}
            className={`h-1.5 w-1.5 rounded-full ${workout.dateCompleted ? "ring-1 ring-white" : ""}`}
            title={`${workout.name}${workout.dateCompleted ? " (Completed)" : ""}`}
            style={{
              backgroundColor: workout.color || "#7bf1a8",
            }}
          />
        )
      })}
    </div>
  )
}

function WorkoutCalendar() {
  const queryClient = useQueryClient()
  const [selectedDate, setSelectedDate] = useState<DateTime | undefined>(DateTime.local())
  const [selectedWorkout, setSelectedWorkout] = useState<TWorkout | null>(null)
  const [expandedExercise, setExpandedExercise] = useState<string | null>(null)
  const [editedWorkoutId, setEditedWorkoutId] = useState<string | null>(null)
  const [editedDate, setEditedDate] = useState<Date | null>(null)

  const { data: workouts, isLoading } = useWorkouts()

  const { mutateAsync } = useMutation({
    mutationFn: async (data: { workoutId: string; dateScheduled: string | null }) => {
      if (!data.dateScheduled) {
        throw new Error("date scheduled is required")
      }
      const response = await apiFetch(`/v1/workouts/${data.workoutId}`, {
        method: "PATCH",
        body: JSON.stringify({
          dateScheduled: data.dateScheduled,
        }),
      })
      return response
    },
    onSuccess: () => {
      setEditedWorkoutId(null)
      setEditedDate(null)
      queryClient.refetchQueries({
        queryKey: fetchWorkoutsQueryOptions.queryKey,
      })
    },
    onError: (error) => {
      if (typeof error === "string") {
        console.error("Error updating workout date:", error)
      }
      console.error("Error updating workout date:", error)
    },
  })

  const { mutateAsync: deleteWorkout } = useMutation({
    mutationFn: async (data: { workoutId: string }) => {
      const response = await apiFetch(`/v1/workouts/${data.workoutId}`, {
        method: "DELETE",
      })
      return response
    },
    onSuccess: () => {
      setEditedWorkoutId(null)
      setEditedDate(null)
      queryClient.refetchQueries({
        queryKey: fetchWorkoutsQueryOptions.queryKey,
      })
    },
    onError: (error) => {
      console.error("Error deleting workout", error)
    },
  })

  // Get workouts for the selected date
  const workoutsOnSelectedDate = selectedDate
    ? workouts?.filter((workout) => {
        // parse workout.date into a Luxon DateTime (assuming ISO-string or JS Date)
        const workoutDate = DateTime.fromISO(workout.dateScheduled)
        // compare only year/month/day against selectedDate
        return workoutDate.hasSame(selectedDate, "day", {
          useLocaleWeeks: false,
        })
      })
    : []

  // Handle date selection
  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(DateTime.fromJSDate(date))
      setSelectedWorkout(null) // Clear selected workout when changing date
    }
  }

  // Handle workout selection
  const handleWorkoutSelect = (workout: TWorkout) => {
    // Toggle selected workout if already selected
    if (selectedWorkout && selectedWorkout.workoutId === workout.workoutId) {
      setSelectedWorkout(null)
    } else {
      setSelectedWorkout(workout)
      setExpandedExercise(null) // Reset expanded exercise state
    }
  }

  // Toggle exercise expansion
  const toggleExercise = (exerciseId: string) => {
    if (expandedExercise === exerciseId) {
      setExpandedExercise(null)
    } else {
      setExpandedExercise(exerciseId)
    }
  }

  return (
    <div className="space-y-6">
      <Card className="workout-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            Workout Calendar
          </CardTitle>
        </CardHeader>
        {isLoading ? (
          <CardContent className="w-full">
            <Skeleton className="h-96 w-full" />
          </CardContent>
        ) : (
          <CardContent className="flex flex-col gap-4 md:flex-row">
            <div className="w-full md:w-1/2">
              <Calendar
                mode="single"
                selected={selectedDate?.toJSDate()}
                onSelect={handleDateSelect}
                modifiers={{
                  //@ts-expect-error This is fine and works for now
                  workout: workouts?.map((workout) => workout.dateScheduled),
                }}
                modifiersClassNames={{
                  workout: "has-workout",
                }}
                components={{
                  //@ts-expect-error This is fine and works for now
                  DayContent: (props: {
                    date: {
                      getDate: () =>
                        | string
                        | number
                        | boolean
                        | React.ReactElement<any, string | React.JSXElementConstructor<any>>
                        | Iterable<React.ReactNode>
                        | React.ReactPortal
                    }
                    dateScheduled: Date
                  }) => (
                    <div className="relative flex h-9 w-9 items-center justify-center">
                      <div>{props.date.getDate()}</div>
                      <DayWithWorkout day={props.date} workouts={workouts ?? []} />
                    </div>
                  ),
                }}
                className="rounded-md border"
              />
            </div>
            <div className="w-full md:w-1/2">
              {selectedDate && !isLoading && (
                <div className="space-y-4">
                  <h3 className="flex items-center gap-2 font-medium text-lg">
                    <span>{selectedDate.toLocaleString(DateTime.DATE_MED)}</span>
                    <Badge variant="outline" className="ml-2">
                      {workoutsOnSelectedDate?.length} workout
                      {workoutsOnSelectedDate?.length !== 1 ? "s" : ""}
                    </Badge>
                  </h3>

                  {(workoutsOnSelectedDate?.length ?? 0) > 0 ? (
                    <div className="space-y-3">
                      {workoutsOnSelectedDate?.map((workout) => {
                        const colorClass = templateColors.default
                        const dotColor = colorClass.replace("bg-", "text-").replace("-300", "-500")

                        return (
                          <div
                            key={workout.workoutId}
                            className={`cursor-pointer rounded-md border p-3 hover:bg-accent ${
                              workout.dateCompleted ? "border-green-300" : ""
                            } ${
                              selectedWorkout?.workoutId === workout.workoutId
                                ? "border-primary ring-1 ring-primary"
                                : ""
                            }`}
                            onClick={() => handleWorkoutSelect(workout)}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <CircleDot className={`h-4 w-4 ${dotColor}`} />
                                <h4 className="font-medium">{workout.name}</h4>
                              </div>
                              {workout.dateCompleted ? (
                                <Badge variant="outline" className="border-green-300 bg-green-50 text-green-700">
                                  <Check className="mr-1 h-3 w-3" />
                                  Completed
                                </Badge>
                              ) : (
                                <Dialog
                                  open={editedWorkoutId !== null}
                                  onOpenChange={(open: boolean) => {
                                    if (!open) {
                                      setEditedWorkoutId(null)
                                    }
                                  }}
                                >
                                  <DialogTrigger asChild>
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        setEditedWorkoutId(workout.workoutId)
                                      }}
                                    >
                                      Edit
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent>
                                    <DialogHeader>
                                      <DialogTitle>Plan a Workout</DialogTitle>
                                      <DialogDescription>
                                        Select a new date for your "{workout.name}" workout.
                                      </DialogDescription>
                                    </DialogHeader>
                                    <div className="py-4">
                                      <Calendar
                                        mode="single"
                                        //@ts-expect-error This is fine and works for now
                                        selected={editedDate}
                                        onSelect={(date) => date && setEditedDate(date)}
                                        modifiers={{
                                          //@ts-expect-error This is fine and works for now
                                          workout: workouts?.map((workout) => workout.dateScheduled),
                                        }}
                                        modifiersClassNames={{
                                          workout: "has-workout",
                                        }}
                                        components={{
                                          //@ts-expect-error This is fine and works for now
                                          DayContent: (props: {
                                            date: {
                                              getDate: () =>
                                                | string
                                                | number
                                                | boolean
                                                | React.ReactElement<any, string | React.JSXElementConstructor<any>>
                                                | Iterable<React.ReactNode>
                                                | React.ReactPortal
                                            }
                                            dateScheduled: Date
                                          }) => (
                                            <div className="relative flex h-9 w-9 items-center justify-center">
                                              <div>{props.date.getDate()}</div>
                                              <DayWithWorkout day={props.date} workouts={workouts ?? []} />
                                            </div>
                                          ),
                                        }}
                                        className="rounded-md border"
                                      />
                                    </div>
                                    <DialogFooter>
                                      <Button variant="outline" onClick={() => setEditedWorkoutId(null)}>
                                        Cancel
                                      </Button>
                                      <Button
                                        variant="destructive"
                                        onClick={async () => {
                                          await deleteWorkout({
                                            workoutId: editedWorkoutId!,
                                          })
                                        }}
                                      >
                                        Cancel Workout
                                      </Button>
                                      <Button
                                        onClick={async () => {
                                          if (!editedDate) {
                                            return
                                          }
                                          await mutateAsync({
                                            workoutId: editedWorkoutId!,
                                            dateScheduled: DateTime.fromJSDate(editedDate).toISODate(),
                                          })
                                        }}
                                      >
                                        Update
                                      </Button>
                                    </DialogFooter>
                                  </DialogContent>
                                </Dialog>
                              )}
                            </div>
                            <div className="mt-1 flex items-center gap-3 text-muted-foreground text-sm">
                              <span className="flex items-center">
                                <Dumbbell size={14} className="mr-1" />
                                {workout.exercises.length} exercise
                                {workout.exercises.length !== 1 ? "s" : ""}
                              </span>
                              {!!workout.dateCompleted && !!workout.dateStarted && (
                                <span className="flex items-center">
                                  <Timer size={14} className="mr-1" />
                                  Duration: {displayDuration(workout.dateStarted!, workout.dateCompleted!)}
                                </span>
                              )}
                              <div className="flex items-center gap-1 font-medium text-workout-secondary">
                                <span>{calculateTotalWeight(workout).toLocaleString()} lbs total</span>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <p className="text-muted-foreground">No workouts planned for this day.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>
      {selectedWorkout && (
        <Card className="workout-detail-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">{selectedWorkout.name}</CardTitle>
              <Badge variant={selectedWorkout.dateCompleted ? "default" : "outline"}>
                {selectedWorkout.dateCompleted ? "Completed" : "Planned"}
              </Badge>
            </div>
            <CardDescription>
              <div className="mt-1 flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <CalendarDays className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedWorkout.dateScheduled}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <Dumbbell className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedWorkout.exercises.length} exercises</span>
                  </div>
                  {selectedWorkout.dateCompleted && selectedWorkout.dateStarted && (
                    <span className="flex items-center">
                      <Timer size={14} className="mr-1" />
                      {displayDuration(selectedWorkout.dateStarted!, selectedWorkout.dateCompleted!)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 font-medium text-workout-secondary">
                  <span>{calculateTotalWeight(selectedWorkout).toLocaleString()} lbs total</span>
                </div>
              </div>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {selectedWorkout.exercises.map((exercise: TExercise, exerciseIndex: number) => (
                <div key={exercise.exerciseId ?? exerciseIndex} className="overflow-hidden rounded-md border">
                  <div
                    className="flex cursor-pointer items-center justify-between p-3 hover:bg-muted/50"
                    onClick={() => toggleExercise(exercise.exerciseId ?? exerciseIndex.toString())}
                  >
                    <div>
                      <h4 className="font-medium">{exercise.name}</h4>
                      <div className="text-muted-foreground text-sm">
                        {exercise.sets.filter((x) => x.isCompleted).length} sets ·{" "}
                        {calculateExerciseWeight(exercise).toLocaleString()} lbs total
                      </div>
                    </div>
                    <div>
                      {expandedExercise === exercise.exerciseId ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>

                  {expandedExercise === exercise.exerciseId && (
                    <div className="px-3 pb-3">
                      <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
                        {exercise.sets
                          .filter((x) => x.isCompleted)
                          .map((set: TExerciseSet, index: number) => (
                            <div key={set.setId ?? `${exerciseIndex}-${index}`} className="rounded-md bg-muted/30 p-2">
                              <div className="font-medium">Set {index + 1}</div>
                              <div className="text-muted-foreground">
                                {set.weight} lbs × {set.reps} reps
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {selectedWorkout.notes && (
                <div className="mt-4 border-t pt-4">
                  <h4 className="mb-2 font-medium">Notes</h4>
                  <p className="whitespace-pre-wrap text-muted-foreground">{selectedWorkout.notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
